import React, {useEffect, useState} from 'react';
import {
  Keyboard,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableWithoutFeedback,
  useWindowDimensions,
  View,
} from 'react-native';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {styles} from './BaaseDropdown.style';
import {useBaseDropdown} from './BaseDropdown.hook';
import {BaseDropdownProps} from './BaseDropdown.types';

const {stateDropdown} = ManageMySiteTestIDs;
const OPTION_ROW_HEIGHT = 44;
const SCREEN_PADDING = 8;
const DROPDOWN_GAP = 4;

export const BaseDropdown: React.FC<BaseDropdownProps> = ({
  value = '',
  onChange,
  options,
  placeholder = '',
  hasError = false,
  errorText = '',
  maxHeight = 200,
  open: controlledOpen,
  onToggle: controlledToggle,
  testID,
  accessibilityLabel,
  accessibilityHint,
  dropdownKey = '',
  disabled = false,
}) => {
  const {
    triggerRef,
    optionRefs,
    coords,
    isOpen,
    isControlled,
    toggle,
    handleOpen,
    updateCoords,
  } = useBaseDropdown({
    value,
    options,
    open: controlledOpen,
    onToggle: controlledToggle,
    dropdownKey,
    disabled,
  });

  const {height: windowHeight} = useWindowDimensions();
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, e => {
      setKeyboardHeight(e.endCoordinates?.height ?? 0);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const selectedOption = options.find(item => item.value === value);
  const triggerLabel =
    selectedOption?.label || placeholder || tt('selectOption', 'Select');
  const triggerAccessibilityLabel = accessibilityLabel ?? triggerLabel;

  const estimatedDropdownHeight = Math.min(
    maxHeight,
    Math.max(1, options.length) * OPTION_ROW_HEIGHT,
  );

  const visibleBottom = Math.max(
    SCREEN_PADDING,
    windowHeight - keyboardHeight - SCREEN_PADDING,
  );

  const defaultTop = coords.y + coords.h + DROPDOWN_GAP;
  const spaceBelow = visibleBottom - defaultTop;
  const spaceAbove = coords.y - SCREEN_PADDING - DROPDOWN_GAP;

  const openUpward =
    spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow;

  const upwardTop = Math.max(
    SCREEN_PADDING,
    coords.y - estimatedDropdownHeight - DROPDOWN_GAP,
  );

  const downwardTop = Math.min(
    defaultTop,
    Math.max(SCREEN_PADDING, visibleBottom - estimatedDropdownHeight),
  );

  const dropdownTop = openUpward ? upwardTop : downwardTop;

  const availableHeight = openUpward
    ? Math.max(120, coords.y - SCREEN_PADDING - DROPDOWN_GAP)
    : Math.max(120, visibleBottom - dropdownTop);

  const dropdownHeight = Math.min(maxHeight, availableHeight);

  const baseTestID = testID ?? stateDropdown.trigger;
  const triggerTestID = `${baseTestID}-trigger`;
  const errorTestID = `${baseTestID}-error`;
  const errorTextTestID = `${baseTestID}-error-text`;
  const optionTestID = (valueKey: string) => `${baseTestID}-option-${valueKey}`;

  return (
    <View>
      <View ref={triggerRef} onLayout={updateCoords}>
        <TouchableOpacity
          style={[styles.trigger, hasError && styles.triggerError]}
          onPress={handleOpen}
          accessibilityRole="button"
          accessibilityLabel={triggerAccessibilityLabel}
          accessibilityHint={
            accessibilityHint ??
            (isOpen
              ? 'Double tap to close options'
              : 'Double tap to open options')
          }
          accessibilityState={{expanded: isOpen, disabled}}
          testID={triggerTestID}>
          <Text
            style={selectedOption ? styles.triggerText : styles.placeholder}
            allowFontScaling={true}
            maxFontSizeMultiplier={2}>
            {triggerLabel}
          </Text>
          <Icon
            name={isOpen ? 'chevron-up' : 'chevron-down'}
            style={styles.iconDropdown}
            accessible={false}
            importantForAccessibility="no"
          />
        </TouchableOpacity>
      </View>

      <Modal
        visible={isOpen}
        transparent
        animationType="none"
        supportedOrientations={[
          'portrait',
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
        onRequestClose={toggle}>
        <TouchableWithoutFeedback
          onPress={toggle}
          accessibilityLabel="Close dropdown"
          accessibilityRole="button"
          accessible={false}
          importantForAccessibility="no">
          <View style={StyleSheet.absoluteFillObject} />
        </TouchableWithoutFeedback>

        <View
          accessibilityViewIsModal
          importantForAccessibility="yes"
          style={[
            styles.listContainer,
            {
              position: 'absolute',
              top: dropdownTop,
              left: coords.x,
              width: coords.w,
            },
          ]}>
          <ScrollView
            style={{maxHeight: dropdownHeight}}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            accessible={false}
            importantForAccessibility="no">
            {options.map(item => {
              const isSelected = value === item.value;

              return (
                <TouchableOpacity
                  key={item.value}
                  ref={ref => {
                    optionRefs.current[item.value] = ref;
                  }}
                  style={styles.listItem}
                  onPress={() => {
                    onChange(item.value);
                    if (!isControlled) {
                      toggle();
                    }
                  }}
                  accessible
                  accessibilityRole="button"
                  accessibilityLabel={item.label}
                  accessibilityState={{selected: isSelected}}
                  testID={optionTestID(item.value)}>
                  <Text
                    allowFontScaling={true}
                    maxFontSizeMultiplier={2}
                    accessible={false}
                    importantForAccessibility="no"
                    style={[
                      styles.listItemText,
                      isSelected && styles.listItemTextSelected,
                    ]}>
                    {item.label}
                  </Text>
                  {isSelected && (
                    <Icon
                      name="check"
                      style={styles.iconDropdownList}
                      accessible={false}
                      importantForAccessibility="no"
                    />
                  )}
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </Modal>

      {hasError && errorText ? (
        <View
          accessible
          accessibilityRole="alert"
          accessibilityLabel={`Dropdown error, ${errorText}`}
          testID={errorTestID}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            accessible={false}
            style={styles.errorText}
            testID={errorTextTestID}>
            {errorText}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

export default BaseDropdown;
